import { WidgetsService } from './WidgetsService';
import { WidgetType } from './WidgetType';
import { blotterWidgetStrategy } from './blotter/BlotterWidgetStrategy';
import { fillsGraphWidgetStrategy } from './fillsGraph/FillsGraphWidgetStrategy';
import { symbolInfoWidgetStrategy } from './symbolInfo/SymbolInfoStrategy';
import { liquiditySummaryWidgetStrategy } from 'widgets/liquiditySummary/widget/LiquiditySummaryWidgetStrategy';
import { portfolioReturnsWidgetStrategy } from './portfolioReturns/PortfolioReturnsWidgetStrategy';
import { intraDayProfileWidgetStrategy } from './intraDayProfile/IntraDayProfileStrategy';
import { portfolioSummaryWidgetStrategy } from './portfolioSummary/PortfolioSummaryWidgetStrategy';
import { strategyParametersWidgetStrategy } from './strategyParams/StrategyParametersStrategy';
import { unknownWidgetStrategy } from './unknown/UnknownWidgetStrategy';
import { venueAnalysisWidgetStrategy } from './venueAnalysis/VenueAnalysisStrategy';
import { openSubmissionsWidgetStrategy } from './openSubmissions/OpenSubmissionsStrategy';
import { expertiseNighthawkWidgetStrategy } from './expertise/expertiseNighthawk/ExpertiseNighthawkWidgetStrategy';
import { expertiseMakeWidgetStrategy } from './expertise/expertiseMake/ExpertiseMakeWidgetStrategy';
import { supportVisualisationWidgetStrategy } from 'widgets/supportVisualisation/SupportVisualisationStrategy';
import { tradeOptimizerWidgetStrategy } from 'widgets/tradeOptimizer/TradeOptimizerStrategy';
import { portfolioPlWidgetStrategy } from 'widgets/portfolioPl/PortfolioPlStrategy';
import { capsuleContainerWidgetStrategy } from 'widgets/tiles/CapsuleContainerWidgetStrategy';
import { symbolTileWidgetStrategy } from 'widgets/symbolTile/SymbolTileWidgetStrategy';
import { openSubmissionsCapsuleWidgetStrategy } from 'widgets/openSubmissions/capsule/OpenSubmissionsCapsuleStrategy';
import { rsiTileWidgetStrategy } from 'widgets/signalTiles/rsiTile/RsiTileWidgetStrategy';
import { pfeTileWidgetStrategy } from 'widgets/signalTiles/pfeTile/PfeTileWidgetStrategy';
import { candlestickTileWidgetStrategy } from 'widgets/candlestickTile/CandlestickTileWidgetStrategy';
import { obvTileWidgetStrategy } from 'widgets/obvTile/ObvTileWidgetStrategy';
import { myPortfolioCapsulesWidgetStrategy } from 'widgets/myPortfolioCapsules/MyPortfolioCapsulesWidgetStrategy';
import { venueTypeCapsuleStrategy } from 'widgets/venueAnalysis/capsules/venueTypeCapsule/VenueTypeCapsuleStrategy';
import { venueExecCapsuleStrategy } from 'widgets/venueAnalysis/capsules/venueExecCapsule/VenueExecCapsuleStrategy';
import { corporateActionsWidgetStrategy } from 'widgets/corpActions/widget/CorporateActionsWidgetStrategy';
import { symbolInfoCapsuleWidgetStrategy } from 'widgets/symbolInfo/capsule/SymbolInfoCapsuleStrategy';
import { volumeCapsuleWidgetStrategy } from 'widgets/liquiditySummary/volumeCapsule/VolumeCapsuleWidgetStrategy';
import { predictedRemainingVolumeCapsuleWidgetStrategy } from 'widgets/liquiditySummary/predictedVolumeCapsule/predictedRemainingVolumeCapsuleWidgetStrategy';
import { corporateActionsCapsuleWidgetStrategy } from 'widgets/corpActions/capsule/CorporateActionsCapsuleWidgetStrategy';
import { outliersStrategy } from 'widgets/outliers/OutliersStrategy';
import { perfVsIndexCapsuleWidgetStrategy } from 'widgets/perfVsIndexCapsule/PerfVsIndexCapsuleStrategy';
import { plCapsuleWidgetStrategy } from 'widgets/plCapsule/PlCapsuleStrategy';
import { predictedCloseVolumeCapsuleWidgetStrategy } from 'widgets/liquiditySummary/predictedVolumeCapsule/predictedCloseVolumeCapsuleWidgetStrategy';
import { orderFillsCapsuleWidgetStrategy } from 'widgets/venueAnalysis/capsules/orderFillsCapsulesShared/orderFillsCapsuleStrategy';
import { SymbolsService } from 'symbols/SymbolsService';
import { estimatedTradeCostWidgetStrategy } from 'widgets/estimatedTradeCost/widget/EstimatedTradeCostWidgetStrategy';
import { estimatedTradeCostComparisonWidgetStrategy } from 'widgets/estimatedTradeCost/widget-comparison/EstimatedTradeCostComparisonWidgetStrategy';
import { orderLiquidityCapsuleStrategy } from 'widgets/venueAnalysis/capsules/orderLiquidityCapsule/orderLiquidityCapsuleStrategy';
import { historicalPercentageChangeCapsuleStrategy } from 'widgets/historicalPercentageChange/capsule/historicalPercentageChangeCapsuleStrategy';
import { historicalPercentageChangeWidgetStrategy } from 'widgets/historicalPercentageChange/widget/historicalPercentageChangeWidgetStrategy';
import { expertiseDnaWidgetStrategy } from 'widgets/expertise/expertiseDna/ExpertiseDnaWidgetStrategy.ts';
import { darkVenueHeatmapStrategy } from 'widgets/darkVenueHeatmap/DarkVenueHeatmapStrategy.tsx';
import { volumeCurveStrategy } from 'widgets/volumeCurve/VolumeCurveStrategy.tsx';
import { orderVolumeProfileStrategy } from 'widgets/orderVolumeProfile/OrderVolumeProfileStrategy';
import { indexEventsWidgetStrategy } from './indexEvents/IndexEventsWidgetStrategy';

export function bootstrapWidgetsService(
    symbolsService: SymbolsService
): WidgetsService {
    return new WidgetsService({
        symbolsService,
        widgets: {
            [WidgetType.BLOTTER]: blotterWidgetStrategy,
            [WidgetType.CANDLESTICK_TILE]: candlestickTileWidgetStrategy,
            [WidgetType.CORPORATE_ACTIONS]: corporateActionsWidgetStrategy,
            [WidgetType.CORPORATE_ACTIONS_CAPSULE]:
                corporateActionsCapsuleWidgetStrategy,
            [WidgetType.DARK_VENUE_HEATMAP]: darkVenueHeatmapStrategy,
            [WidgetType.EST_TRADE_COST_WIDGET]:
                estimatedTradeCostWidgetStrategy,
            [WidgetType.EST_TRADE_COST_COMPARISON_WIDGET]:
                estimatedTradeCostComparisonWidgetStrategy,
            [WidgetType.EXPERTISE_MAKE]: expertiseMakeWidgetStrategy,
            [WidgetType.EXPERTISE_NIGHTHAWK]: expertiseNighthawkWidgetStrategy,
            [WidgetType.EXPERTISE_DNA]: expertiseDnaWidgetStrategy,
            [WidgetType.FILLS_GRAPH]: fillsGraphWidgetStrategy,
            [WidgetType.INDEX_EVENTS]: indexEventsWidgetStrategy,
            [WidgetType.LIQUIDITY_SUMMARY]: liquiditySummaryWidgetStrategy,
            [WidgetType.HistoricalPercentageChangeCapsule]:
                historicalPercentageChangeCapsuleStrategy,
            [WidgetType.HistoricalPercentageChangeWidget]:
                historicalPercentageChangeWidgetStrategy,
            [WidgetType.OBV_TILE]: obvTileWidgetStrategy,
            [WidgetType.ORDER_FILLS_BY_VENUE_CAPSULE]:
                orderFillsCapsuleWidgetStrategy(
                    WidgetType.ORDER_FILLS_BY_VENUE_CAPSULE
                ),
            [WidgetType.ORDER_FILLS_BY_VENUE_TYPE_CAPSULE]:
                orderFillsCapsuleWidgetStrategy(
                    WidgetType.ORDER_FILLS_BY_VENUE_TYPE_CAPSULE
                ),
            [WidgetType.ORDER_LIQUIDITY_CAPSULE]: orderLiquidityCapsuleStrategy,
            [WidgetType.ORDER_VOLUME_PROFILE]: orderVolumeProfileStrategy,
            [WidgetType.OUTLIERS_WIDGET]: outliersStrategy,
            [WidgetType.PORTFOLIO_PL]: portfolioPlWidgetStrategy,
            [WidgetType.PORTFOLIO_RETURNS]: portfolioReturnsWidgetStrategy,
            [WidgetType.PORTFOLIO_SUMMARY]: portfolioSummaryWidgetStrategy,
            [WidgetType.PREDICTED_CLOSE_VOLUME_CAPSULE]:
                predictedCloseVolumeCapsuleWidgetStrategy,
            [WidgetType.PREDICTED_VOLUME_CAPSULE]:
                predictedRemainingVolumeCapsuleWidgetStrategy,
            [WidgetType.PFE_TILE]: pfeTileWidgetStrategy,
            [WidgetType.RSI_TILE]: rsiTileWidgetStrategy,
            [WidgetType.INTRA_DAY_PROFILE]: intraDayProfileWidgetStrategy,
            [WidgetType.MY_PORTFOLIO_CAPSULES]:
                myPortfolioCapsulesWidgetStrategy,
            [WidgetType.OPEN_SUBMISSIONS]: openSubmissionsWidgetStrategy,
            [WidgetType.OPEN_SUBMISSIONS_CAPSULE]:
                openSubmissionsCapsuleWidgetStrategy,
            [WidgetType.PERF_VS_INDEX_CAPSULE]:
                perfVsIndexCapsuleWidgetStrategy,
            [WidgetType.PL_CAPSULE]: plCapsuleWidgetStrategy,
            [WidgetType.SUPPORT_VISUALISATION]:
                supportVisualisationWidgetStrategy,
            [WidgetType.STRATEGY_PARAMETERS]: strategyParametersWidgetStrategy,
            [WidgetType.TRADE_OPTIMIZER]: tradeOptimizerWidgetStrategy,
            [WidgetType.SYMBOL_INFO]: symbolInfoWidgetStrategy,
            [WidgetType.SYMBOL_INFO_CAPSULE]: symbolInfoCapsuleWidgetStrategy,
            [WidgetType.SYMBOL_TILE]: symbolTileWidgetStrategy,
            [WidgetType.TILES]: capsuleContainerWidgetStrategy,
            [WidgetType.UNKNOWN]: unknownWidgetStrategy,
            [WidgetType.VENUE_ANALYSIS]: venueAnalysisWidgetStrategy,
            [WidgetType.VENUE_EXEC_CAPSULE]: venueExecCapsuleStrategy,
            [WidgetType.VENUE_TYPE_CAPSULE]: venueTypeCapsuleStrategy,
            [WidgetType.VOLUME_CAPSULE]: volumeCapsuleWidgetStrategy,
            [WidgetType.VOLUME_CURVE]: volumeCurveStrategy,
        },
    });
}
